import { graphql } from 'react-apollo'
import { withRouter } from 'react-router'

import { ModalActionTypes } from '@acorns/web-components'
import { withFormik } from 'formik'
import QueryString from 'query-string'
import { filter, isEmpty, omit, pathOr } from 'ramda'
import { branch, compose, lifecycle, renderComponent, withHandlers, withProps } from 'recompose'

import CenteredSpinner from 'components/CenteredSpinner'
import withAgreements from 'components/HOCs/WithAgreements'
import withHeroText from 'components/HOCs/withHeroText'
import withPartnerProps from 'components/HOCs/WithPartnerProps'
import withReferralProp from 'components/HOCs/WithReferralProp'
import withRemoteStrings from 'components/HOCs/withRemoteStrings'
import getDefaultEventData from 'components/Trackers/defaults'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { getButtonTappedEvent } from 'utils/analytics'
import trackEvent from 'utils/analytics/trackEvent'
import { withApolloErrorModal } from 'utils/Apollo'
import { withEnv } from 'utils/environment'
import { withFlowRouter } from 'utils/FlowProvider'
import { getFlow } from 'utils/FlowProvider/utils'
import { withPrompt } from 'utils/prompts'
import withTieredRegistration from 'utils/TieredRegistration'

import { disclosureRows, handleFormSubmit, identityRows, mapPropsToValues } from './form'
import createUser from './gql/createUser.mutation.gql'
import updatePersonalInfoMutation from './gql/updatePersonalInfo.mutation.gql'
import updateRoundUpProfile from './gql/updateRoundUpProfile.mutation.gql'
import { Props } from './types'
import { validate, validationErrorMap, validationSchema } from './validation'

export const enhance = compose<Props, {}>(
  withEnv,
  withFlowRouter,
  withApolloErrorModal,
  withPartnerProps,
  withAgreements,
  withReferralProp,
  withPrompt,
  withHeroText,
  withRouter,
  withRemoteStrings,
  withTieredRegistration,
  graphql(createUser, { name: 'createUser' }),
  graphql(updatePersonalInfoMutation, {
    name: 'updatePersonalInfo',
  }),
  graphql(updateRoundUpProfile, {
    name: 'updateRoundUpProfile',
  }),
  withHandlers({
    getSourceAttributes: ({ partner }) => () => {
      // Determine the source of this customer's arrival to Acorns
      let attributes = QueryString.parse(window.location.search.split('?')[1])
      let id = pathOr(attributes.s1, ['org', 'name'], partner)

      let flow = getFlow()
      if (flow === 'tiered') {
        flow = 'tiered_reg'
      }

      // Note: If the `clickid` query param exists set the source.id as `impactradius`
      // Note: This should really be done by the backend.
      /* istanbul ignore else */
      if (attributes.clickid) {
        id = 'impactradius'
      }

      // Note We do this because source_id = s1
      attributes = omit(['s1'], attributes)
      attributes = { ...attributes, flow }

      // Convert attributes to a JSON string
      attributes = JSON.stringify(attributes)

      return {
        id,
        attributes,
      }
    },
    updateUserProfile: ({ partner, updatePersonalInfo }) => (email: string) => {
      // Update user profile
      const partnerUserData = filter(key => key !== null && key !== '', partner.user)

      // Always use the email address entered by the user, not the one provided
      // by the partner (if provided)
      partnerUserData.email = email

      return new Promise(resolve => {
        // Don't bother with updating the user profile if none was provided by the partner
        /* istanbul ignore else */
        if (isEmpty(partnerUserData)) {
          resolve()
        } else {
          // Update user profile data
          updatePersonalInfo({ variables: partnerUserData }).then(() => {
            resolve()
          })
        }
      })
    },
  }),
  withFormik({
    enableReinitialize: true,
    isInitialValid: true,
    mapPropsToValues,
    handleSubmit: handleFormSubmit,
    validate,
    validateOnChange: false,
  }),
  withProps(({ openAgreementModal, partner, history, getRemoteString, heroText }) => ({
    disclosureRows: disclosureRows(openAgreementModal, partner),
    identityRows,
    pageNotFound: pathOr(false, ['location', 'state', 'pageNotFound'], history),
    header: getRemoteString('create_account_header', heroText.header),
    text: getRemoteString('create_account_text', heroText.text),
  })),
  lifecycle<Props, any>({
    componentDidMount() {
      this.props.loadDataWithGQL()
    },
  }),
  withHandlers<Props, Partial<Props>>({
    setFieldValue: ({ setFieldValue }) => (field, value) => {
      setFieldValue(field, value, true)
    },
    onSubmit: props => () => {
      const { promptUser, submitCount, submitForm, values } = props

      // Attempt submit
      submitForm()

      // If the number of attempts exceeds 2, display a dialog
      if (submitCount > 2) {
        validationSchema(props)
          .validate(values)
          .catch(error => {
            const message = validationErrorMap.get(error.path)
            promptUser({
              title: 'Missed One Step',
              message,
              actions: [
                {
                  type: ModalActionTypes.primary,
                  label: 'Okay',
                },
              ],
            })
          })
      }
    },
    onChange: props => async e => {
      // https://github.com/jaredpalmer/formik/issues/1218#issuecomment-451119016
      await props.handleChange(e)

      if (e.target.id === 'usResident') {
        trackEvent('Button Tapped', {
          eventData: {
            ...getButtonTappedEvent(TRACKER_IDS.SIGNUP.US_RESIDENT_AGREEMENT_BUTTON),
            ...getDefaultEventData('Button Tapped'),
          },
        })
      }

      // Only validate the form if the user has attempted at least one submit
      if (props.submitCount > 0) {
        props.validateForm()
      }
    },
    handleOnLink: ({ env }) => () => {
      const url = env.get('webAppUrl')
      window.location.replace(url)
    },
  }),
  branch(({ isPartnerReg, partner }) => isPartnerReg && !partner, renderComponent(CenteredSpinner)),
)
